import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import React, { useMemo } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import store, { persistor } from './store';
import Theme from 'components/template/Theme';
import Layout from 'components/layout';
import AnalyticsBootstrap from 'components/shared/AnalyticsBootstrap';
import history from './history';
import { useStorageProvider } from 'utils/storageMedia';
import './locales';
import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  ApolloLink,
  ApolloProvider,
  concat,
  split,
} from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import { loadErrorMessages, loadDevMessages } from '@apollo/client/dev';

const environment = process.env.NODE_ENV;

if (environment !== 'production') {
  loadDevMessages();
  loadErrorMessages();
}

function App() {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ClientContainer>
          <StorageProviderLoader />
          <BrowserRouter history={history}>
            <AnalyticsBootstrap />
            <Theme>
              <Layout />
            </Theme>
          </BrowserRouter>
        </ClientContainer>
      </PersistGate>
    </Provider>
  );
}

// Mantém o provedor de storage ativo (core.storage_provider) em cache global,
// para que o helper rewriteMediaUrl funcione em toda a aplicação.
const StorageProviderLoader = () => {
  useStorageProvider();
  return null;
};

export const ClientContainer = ({ children }) => {
  // Usar o endpoint de proxy do service ao invés do Hasura diretamente
  const uri = `${process.env.REACT_APP_SERVICE_API}/api/graphql`;

  // Criar o Apollo Client apenas uma vez, usando uma função para obter o token atual
  // Isso evita recriar o client quando o token muda, o que cancelaria requisições em andamento
  const client = useMemo(() => {
    // Função para obter headers atualizados com o token mais recente
    const getAuthHeaders = () => {
      const currentToken = localStorage.getItem('token');
      const headers = {
        'content-type': 'application/json',
      };
      if (currentToken) {
        headers.Authorization = `Bearer ${currentToken}`;
      }
      return headers;
    };

    // Para subscriptions WebSocket, ainda precisamos acessar o Hasura diretamente
    // mas sem o admin secret. O Hasura deve validar pelo JWT token
    const httpLink = new HttpLink({ uri });
    const wsLink = new GraphQLWsLink(
      createClient({
        url: 'wss://drderma.hasura.app/v1/graphql',
        connectionParams: () => ({
          headers: getAuthHeaders(),
        }),
      })
    );

    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        );
      },
      wsLink,
      httpLink
    );

    // Middleware de autenticação que busca o token atual a cada requisição
    // Isso garante que sempre use o token mais recente sem recriar o client
    const authMiddleware = new ApolloLink((operation, forward) => {
      operation.setContext(({ headers: ctxHeaders = {} }) => ({
        headers: {
          ...ctxHeaders,
          ...getAuthHeaders(),
        },
      }));
      return forward(operation);
    });

    return new ApolloClient({
      uri,
      cache: new InMemoryCache(),
      link: concat(authMiddleware, splitLink),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'cache-and-network',
          errorPolicy: 'all',
        },
        query: {
          fetchPolicy: 'network-only',
          errorPolicy: 'all',
        },
        mutate: {
          errorPolicy: 'all',
        },
      },
    });
  }, []); // Dependências vazias - client é criado apenas uma vez

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default App;
